import { Box, Button, Stack } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import moment from "moment";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { SubscriptionContext } from "../../../contexts/SubscriptionContext";
import { getUserEmail } from "../../../helpers/authUser";
import { scrollbarStyle } from '../../../shared/dashboard';
import EnterprisePlan from '../../molecules/subscription-plans/EnterprisePlan';
import GrowthPlan from '../../molecules/subscription-plans/GrowthPlan';
import ProPlan from "../../molecules/subscription-plans/ProPlan";

export enum SubscriptionPlan {
    Free = 'free',
    Pro_v1 = 'pro_v1_monthly',
    Pro_v2 = 'pro_v2_monthly',
    Pro_Legacy = 'charlie',
    Growth_v1 = 'growth_v1_monthly',
    Growth_v2 = 'growth_v2_monthly',
    PowerUser = 'power_v1_monthly',
    Enterprise = 'enterprise_v1_monthly',
};

export enum SubscriptionSeatLimit {
    /* cont-tf-mig */
    price_1S6ck4C4CyYG72UKj0LqlJKX = 1,
    price_1S6cqlC4CyYG72UKE8oCR2U0 = 3,
    // price_1S8WV3C4CyYG72UKMZmPe6d6 = 6,
    /* app */
    price_1S6dcdC4CyYG72UKvVlU2ac4 = 1,
    price_1S6dcZC4CyYG72UK5UaG7eNM = 3,
    price_1S8WV3C4CyYG72UKMZmPe6d6 = 6,
};

export enum SubscriptionMonthlyLimit {
    Free = 0,
    Pro_v1 = 100,
    Pro_v2 = 100,
    Pro_Legacy = 50,
    Growth_v1 = 300,
    Growth_v2 = 300,
    PowerUser = +Infinity,
    Enterprise = +Infinity,
};

const useStyles = makeStyles((theme) => ({
    planCard: {
        position: 'relative',
        display: 'inline-block',
        width: '100%',
        minWidth: 350,
        maxWidth: 400,
        height: 550,
        marginTop: 8,
        borderRadius: 32,
        padding: '16px 24px',
        border: `2px solid ${theme.colors.neutral['200']}`,
        boxShadow: '0px 4px 10px -1px rgba(16, 24, 40, 0.06)',
        transition: '0.05s ease-in-out',
        "&:hover": {
            boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40, 0.14), 0px 1px 14px 0px rgba(16, 24, 40, 0.12)',
        },
    },
    selectionMark: {
        position: 'relative',
        width: 60,
        height: 40,
        bottom: `calc(100% + 30px)`,
        left: `calc(100% - 150px)`,
    },
    currentPlan: {
        width: 150,
        height: 40,
        borderRadius: 32,
        borderWidth: 3,
        textTransform: 'none',
        padding: '8px 24px',
        fontFamily: 'Lato',
        fontSize: '1.1rem',
        fontWeight: 'bold',
    },
}));

const CurrentPlan: React.FC<{}> = () => {
    const classes = useStyles();

    return (
        <Box className={classes.selectionMark}>
            <Button variant="contained" className={classes.currentPlan}>
                {'Current plan'}
            </Button>
        </Box>
    );
};

const SubscriptionSelector: React.FC<{
    onShowManage: (state: boolean) => void,
    onSelectSubscription: () => void,
}> = ({ onShowManage }) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const { subscription } = useContext(SubscriptionContext);

    const [loaded, setLoaded] = useState<boolean>(false);
    const [proPlanSelect, setProPlanSelect] = useState<boolean>(false);
    const [growthPlanSelect, setGrowthPlanSelect] = useState<boolean>(false);
    const [powerPlanSelect, setPowerPlanSelect] = useState<boolean>(false);
    const [enterprisePlanSelect, setEnterprisePlanSelect] = useState<boolean>(false)

    const [trialDays, setTrialDays] = useState<number | undefined>(undefined);
    const [admin, setAdmin] = useState<string>('');

    const handleSelectPlan = useCallback((plan: SubscriptionPlan|string) => {
        switch (plan) {
            case SubscriptionPlan.Free:
                setProPlanSelect(false);
                setGrowthPlanSelect(false);
                setPowerPlanSelect(false);
                setEnterprisePlanSelect(false);
                break;
            default:
                setProPlanSelect((plan === SubscriptionPlan.Pro_v1) || (plan === SubscriptionPlan.Pro_v2) || (plan === SubscriptionPlan.Pro_Legacy));
                setGrowthPlanSelect((plan === SubscriptionPlan.Growth_v1) || (plan === SubscriptionPlan.Growth_v2));
                setPowerPlanSelect(plan === SubscriptionPlan.PowerUser);
                setEnterprisePlanSelect(plan === SubscriptionPlan.Enterprise);
                setEnterprisePlanSelect(plan.includes('enterprise_'));
            break;
        }
    }, []);

    const handleSubscription = useCallback((subscription: any) => {
        if (subscription?.cancelAt) {
            const fromCancelDiff = moment(subscription?.cancelAt).diff(moment(), 'days');
            if (fromCancelDiff >= 0)
                setTrialDays(fromCancelDiff);
            handleSelectPlan(subscription.planName.toLowerCase() as SubscriptionPlan);
        } else if (subscription.status === 'trialing') {
            let trialDaysCount = 0;
            if (subscription?.trialEndedAt)
                trialDaysCount = moment(subscription?.trialEndedAt).diff(moment(), 'days');
            if (trialDaysCount >= 0 && subscription?.paymentMethodId) {
                setTrialDays(trialDaysCount);
                handleSelectPlan(subscription.planName.toLowerCase() as SubscriptionPlan);
            } else if (trialDaysCount >= 0) {
                setTrialDays(trialDaysCount);
                handleSelectPlan(subscription.planName.toLowerCase() as SubscriptionPlan);
            } else if (subscription?.paymentMethodId) {
                handleSelectPlan(subscription.planName.toLowerCase() as SubscriptionPlan);
            }
        } else if (subscription.status === 'active') {
            handleSelectPlan(subscription.planName.toLowerCase() as SubscriptionPlan);
        }
    }, [handleSelectPlan]);

    useEffect(() => {
        onShowManage(loaded
            && (proPlanSelect || growthPlanSelect || powerPlanSelect || enterprisePlanSelect)
            && (admin === getUserEmail(user).toLowerCase()));
    }, [loaded, proPlanSelect, growthPlanSelect, powerPlanSelect, enterprisePlanSelect, admin, user, onShowManage]);

    useEffect(() => {
        if (subscription) {
            switch (subscription.planName?.toLowerCase()) {
                case SubscriptionPlan.Enterprise:
                case SubscriptionPlan.PowerUser:
                case SubscriptionPlan.Growth_v1:
                case SubscriptionPlan.Growth_v2:
                case SubscriptionPlan.Pro_v1:
                case SubscriptionPlan.Pro_v2:
                case SubscriptionPlan.Pro_Legacy:
                    handleSubscription(subscription);
                    break;
                case SubscriptionPlan.Free:
                    handleSelectPlan(SubscriptionPlan.Free);
                    break;
                default:
                    if (subscription.planName?.includes('enterprise_'))
                        handleSubscription(subscription);
                    break;
            }
            setAdmin(subscription?.user!);
            setLoaded(true);
        }
    }, [handleSelectPlan, handleSubscription, subscription, user]);

    return (<>
        <Stack direction="column" alignItems="center" justifyContent="center" pt={3} width="100%">
            <Stack direction="row" spacing={8} alignItems="center" justifyContent="flex-start" width="auto" p={3} px={4}
                sx={{ overflowX: 'auto', ...scrollbarStyle }}>
                <Stack direction="column" className={classes.planCard}>
                    <ProPlan selected={proPlanSelect}
                        admin={admin}
                        daysLeft={proPlanSelect ? trialDays : undefined} />
                    {proPlanSelect && (<CurrentPlan />)}
                </Stack>
                <Stack direction="column" className={classes.planCard}>
                    <GrowthPlan selected={growthPlanSelect}
                        admin={admin}
                        daysLeft={growthPlanSelect? trialDays : undefined} />
                    {growthPlanSelect && (<CurrentPlan />)}
                </Stack>
                <Stack direction="column" className={classes.planCard}>
                    <EnterprisePlan selected={enterprisePlanSelect}
                        admin={admin}
                        price={(subscription?.pricePerSeatCents || 0) / 100}
                        seats={subscription?.planId! in SubscriptionSeatLimit ? SubscriptionSeatLimit[subscription?.planId as keyof typeof SubscriptionSeatLimit] : 1} />
                    {enterprisePlanSelect && (<CurrentPlan />)}
                </Stack>
            </Stack>
        </Stack>
    </>);
};

export default SubscriptionSelector;